import React, { ReactElement } from "react";
import { ApolloError, gql, useMutation } from "@apollo/client";
import { UpdateCustomerMutation, UpdateCustomerMutationVariables } from "../../../schemaTypes";
import { normalizeErrors } from "../../../utils/normalizeErrors";
import { NormalizedErrorMap } from "../../../types/NormalizedErrorMap";

const updateCustomerMutation = gql`
  mutation UpdateCustomer($input: UpdateCustomerInput!) {
    updateCustomer(input: $input) {
      errors {
        path
        message
      }
      message
    }
  }
`;

export interface WithUpdateCustomer {
  updateCustomer: (
    values: UpdateCustomerMutationVariables
  ) => Promise<{ errors: NormalizedErrorMap | null; message?: string | null }>;
}

interface Props {
  children: (data: WithUpdateCustomer) => ReactElement | null;
}

export const UpdateCustomer: React.FC<Props> = ({ children }) => {
  const [mutate] = useMutation<
    UpdateCustomerMutation,
    UpdateCustomerMutationVariables
  >(updateCustomerMutation);

  const updateCustomer = React.useCallback(async (
    values: UpdateCustomerMutationVariables
  ) => {
    try {
      const response = await mutate({ variables: values });
      const result = response.data?.updateCustomer;

      if (!result) {
        return {
          errors: { general: "No response from server." },
          message: undefined,
        };
      }

      return {
        errors: result.errors
          ? normalizeErrors(
              result.errors.filter(
                (entry): entry is { path: string; message: string } => entry !== null
              )
            )
          : null,
        message: result.message,
      };
    } catch (error) {
      const apolloError = error as ApolloError;
      const message =
        apolloError.graphQLErrors?.[0]?.message ||
        apolloError.message ||
        "An error occurred";
      const normalizedMessage = message.toLowerCase();

      if (normalizedMessage.includes("username")) {
        return { errors: { username: message }, message: undefined };
      }

      if (normalizedMessage.includes("gender")) {
        return { errors: { gender: message }, message: undefined };
      }

      if (normalizedMessage.includes("community")) {
        return { errors: { communityId: message }, message: undefined };
      }

      if (normalizedMessage.includes("dob") || normalizedMessage.includes("under") || normalizedMessage.includes("18")) {
        return { errors: { dob: message }, message: undefined };
      }

      if (normalizedMessage.includes("address")) {
        return { errors: { address: message }, message: undefined };
      }

      return { errors: { general: message }, message: undefined };
    }
  }, [mutate]);

  return children({ updateCustomer });
};
